import { KebabMenu, KebabMenuItem } from '@library/components/menus';
import type {
  MemberHeaderVisibleMenuItems,
  MemberOverlayName
} from '@library/types/members';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BankWhiteIcon from '@library/assets/images/icons/bank-white.svg';
import BookIcon from '@library/assets/images/icons/book-icon.svg';
import CheckIcon from '@library/assets/images/icons/checked-circle-icon.svg';
import EditIcon from '@library/assets/images/icons/edit-icon.svg';
import EmailIcon from '@library/assets/images/icons/email-icon.svg';
import HandIcon from '@library/assets/images/icons/hand-icon.svg';
import PersonIcon from '@library/assets/images/icons/person-icon.svg';
import RibbonIcon from '@library/assets/images/icons/ribbon-white-icon.svg';
import UserAddIcon from '@library/assets/images/icons/user-add-icon.svg';
import UserRemoveIcon from '@library/assets/images/icons/user-remove-icon.svg';
import { cn } from '@library/component-utils/cn';

export interface MemberAdditionalActionsKebabMenuProps {
  isDeactivated: boolean;
  menuItemsClassName?: string;
  onShowModal: (value: MemberOverlayName) => void;
  visibleMenuItems: MemberHeaderVisibleMenuItems;
}

export interface MemberActionsMenuItem {
  className?: string;
  handleOnClick: () => void;
  message: string;
  icon: string;
  iconClassName?: string;
  showMenuItem?: boolean;
}

export const MemberAdditionalActionsKebabMenu = ({
  isDeactivated,
  menuItemsClassName = '',
  onShowModal,
  visibleMenuItems
}: MemberAdditionalActionsKebabMenuProps) => {
  const {
    createAdjustment,
    editAccountInformation,
    kindbridgeEnrollment,
    manageLegacyRuleset,
    manageMemberGroups,
    manageRuleset,
    manageTinCollection,
    manageVipStatus,
    manuallyPassMember,
    resetPassword,
    updateMemberActivation
  } = visibleMenuItems;

  const { memberId } = useParams();
  const navigate = useNavigate();

  const handleShowModal = useCallback(
    (modalName: MemberOverlayName) => {
      onShowModal(modalName);
    },
    [onShowModal]
  );

  const handleClickRuleset = useCallback(
    () => navigate(`/rulesets?memberId=${memberId}`),
    [navigate, memberId]
  );

  const menuItems = useMemo<MemberActionsMenuItem[]>(
    () => [
      {
        handleOnClick: () => handleShowModal('EDIT_ACCOUNT_INFORMATION'),
        message: 'Edit account information',
        icon: EditIcon,
        showMenuItem: editAccountInformation
      },
      {
        handleOnClick: () => handleShowModal('MANAGE_MEMBER_GROUPS'),
        message: 'Manage member groups',
        icon: PersonIcon,
        iconClassName: 'mb-[0.5px]',
        showMenuItem: manageMemberGroups
      },
      {
        handleOnClick: () => handleShowModal('MANAGE_LEGACY_RULESET'),
        message: 'Manage ruleset',
        icon: PersonIcon,
        iconClassName: 'mb-[0.5px]',
        showMenuItem: manageLegacyRuleset
      },
      {
        handleOnClick: handleClickRuleset,
        message: 'Configure member ruleset',
        icon: BookIcon,
        showMenuItem: manageRuleset
      },
      {
        handleOnClick: () => handleShowModal('CREATE_ADJUSTMENT'),
        message: 'Create adjustment',
        icon: EditIcon,
        showMenuItem: createAdjustment
      },
      {
        handleOnClick: () => handleShowModal('RESET_PASSWORD'),
        message: 'Reset password',
        icon: EmailIcon,
        showMenuItem: resetPassword
      },
      {
        handleOnClick: () => handleShowModal('MANAGE_TIN_COLLECTION'),
        message: 'Update TIN collection',
        icon: BankWhiteIcon,
        showMenuItem: manageTinCollection
      },
      {
        handleOnClick: () => handleShowModal('MANUALLY_PASS_MEMBER'),
        message: 'Manually pass member',
        icon: CheckIcon,
        showMenuItem: manuallyPassMember
      },
      {
        handleOnClick: () => handleShowModal('MANAGE_VIP_STATUS'),
        message: 'Toggle VIP status',
        icon: RibbonIcon,
        iconClassName: 'mb-[0.5px]',
        showMenuItem: manageVipStatus
      },
      {
        handleOnClick: () => handleShowModal('ENROLL_KINDBRIDGE'),
        message: 'Enroll in Kindbridge',
        icon: HandIcon,
        showMenuItem: kindbridgeEnrollment
      },
      {
        className: isDeactivated ? 'text-atlien-100' : 'text-roses',
        handleOnClick: () => handleShowModal('UPDATE_MEMBER_ACTIVATION'),
        message: `${isDeactivated ? 'Reactivate' : 'Deactivate'} member`,
        icon: isDeactivated ? UserAddIcon : UserRemoveIcon,
        showMenuItem: updateMemberActivation
      }
    ],
    [
      createAdjustment,
      editAccountInformation,
      handleClickRuleset,
      handleShowModal,
      isDeactivated,
      kindbridgeEnrollment,
      manageLegacyRuleset,
      manageMemberGroups,
      manageRuleset,
      manageTinCollection,
      manageVipStatus,
      manuallyPassMember,
      resetPassword,
      updateMemberActivation
    ]
  );

  const MenuItems = () => {
    return menuItems.map((menuItem) => {
      const {
        className = '',
        handleOnClick,
        icon,
        iconClassName = '',
        message,
        showMenuItem = true
      } = menuItem;

      if (!showMenuItem) {
        return null;
      }

      return (
        <KebabMenuItem
          key={message}
          className={cn(
            'text-soClean-100 hover:bg-soFresh-140 flex h-10 w-full items-center gap-2 px-2 pl-3',
            className
          )}
          handleOnClick={handleOnClick}
        >
          <img
            alt={message}
            className={cn('size-4', iconClassName)}
            src={icon}
          />
          <span>{message}</span>
        </KebabMenuItem>
      );
    });
  };

  return (
    <KebabMenu
      className="relative size-12"
      menuButtonClassName="self-center size-12 hover:bg-soFresh-130 rounded-md flex items-center justify-center"
      menuItemsClassName={cn(
        'border border-soFresh-140 bg-soFresh-110 z-10 text-xs w-52 rounded-md right-0 mt-2',
        menuItemsClassName
      )}
      menuButtonOpenClassName="rounded-md bg-soFresh-140"
      title="MemberAdditionalActionsKebabMenu"
      testId="member-additional-actions-kebab-menu"
    >
      <MenuItems />
    </KebabMenu>
  );
};
